import type { FC } from 'react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { Typography } from '@pflegenavi/web-components';
import DownloadIcon from '@mui/icons-material/Download';
import type { OpenItem } from '@pflegenavi/service-provider-frontend/api';
import { useGetOpenItems } from '@pflegenavi/service-provider-frontend/api';
import {
  csvDownload,
  useGetBatchReceiptDateString,
} from '@pflegenavi/shared/frontend';
import { dateAsYYYY_MM_DD } from '@pflegenavi/shared/utils';
import { useFormatting } from '@pflegenavi/frontend/localization';

/**
 * Filters open items for the CSV export. Deleted receipts (whose entry survives
 * on the service-provider side but whose receipt was removed on the nursing home
 * side) are excluded unless the user opts to include them.
 */
export const filterOpenItemsForCsv = (
  items: OpenItem[],
  includeDeleted: boolean
): OpenItem[] =>
  includeDeleted ? items : items.filter((item) => item.status !== 'deleted');

export const OpenItemsDownload: FC = () => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<DownloadIcon />}
        onClick={() => setDialogOpen(true)}
      >
        {t('accounting.open-items.download-button')}
      </Button>
      <OpenItemsDownloadDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
};

interface OpenItemsDownloadDialogProps {
  open: boolean;
  onClose: () => void;
}

const OpenItemsDownloadDialog: FC<OpenItemsDownloadDialogProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [isDownloading, setIsDownloading] = useState(false);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const { getBatchReceiptDateString } = useGetBatchReceiptDateString();
  const { fCsvNumberCents } = useFormatting();

  const { refetch } = useGetOpenItems({ enabled: false });

  const getStatusTranslation = useCallback(
    (status: OpenItem['status']): string =>
      t(`accounting.open-items.status.${status}`),
    [t]
  );

  const formatBatchDetails = useCallback(
    (entry: OpenItem): string => {
      if (!entry.batch_entry_count) {
        return '';
      }

      const dateStr = getBatchReceiptDateString(
        entry.batch_use_individual_dates ?? false,
        entry.batch_min_receipt_date ?? undefined,
        entry.batch_max_receipt_date ?? undefined,
        entry.batch_receipt_date ?? undefined
      );
      const countStr = t(
        'service-provider-app.receipt-batch-overview-table.receipts-count',
        { count: entry.batch_entry_count }
      );

      return `${dateStr} - ${countStr}`;
    },
    [getBatchReceiptDateString, t]
  );

  const handleDownload = useCallback(async () => {
    setIsDownloading(true);
    try {
      const result = await refetch();
      if (result.error) {
        enqueueSnackbar(t('dialog.open-items.download-error'), {
          variant: 'error',
        });
        return;
      }
      if (result.data) {
        const entries = filterOpenItemsForCsv(result.data.data, includeDeleted);
        const csvData = entries.map((entry) => ({
          nursing_home_name: entry.nursing_home?.nursing_home_name ?? '',
          nursing_home_tenant: entry.nursing_home?.tenant ?? '',
          batch_details: formatBatchDetails(entry),
          receipt_date:
            entry.receipt_date ?? entry.batch_receipt_date
              ? dateAsYYYY_MM_DD(
                  (entry.receipt_date ?? entry.batch_receipt_date) as Date
                )
              : '',
          amount: fCsvNumberCents(entry.amount),
          resident_firstname: entry.resident?.firstname ?? '',
          resident_lastname: entry.resident?.lastname ?? '',
          status: getStatusTranslation(entry.status),
          embargo_date: entry.embargo_date
            ? dateAsYYYY_MM_DD(entry.embargo_date)
            : '',
        }));

        csvDownload({
          data: csvData,
          filename: `open-items-${dateAsYYYY_MM_DD(new Date())}.csv`,
          headers: [
            t('csv.open-items.headers.nursing-home-name'),
            t('csv.open-items.headers.nursing-home-tenant'),
            t('csv.open-items.headers.batch-details'),
            t('csv.open-items.headers.receipt-date'),
            t('csv.open-items.headers.amount'),
            t('csv.open-items.headers.resident-firstname'),
            t('csv.open-items.headers.resident-lastname'),
            t('csv.open-items.headers.status'),
            t('csv.open-items.headers.embargo-date'),
          ],
        });
        onClose();
      }
    } finally {
      setIsDownloading(false);
    }
  }, [
    refetch,
    t,
    onClose,
    enqueueSnackbar,
    getStatusTranslation,
    formatBatchDetails,
    fCsvNumberCents,
    includeDeleted,
  ]);

  return (
    <Dialog maxWidth="xs" fullWidth open={open} onClose={onClose}>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5">{t('dialog.open-items.title')}</Typography>
      </DialogTitle>
      <DialogContent sx={{ pb: 0 }}>
        <Typography variant="body2">
          {t('dialog.open-items.content')}
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
            />
          }
          label={t('dialog.open-items.include-deleted')}
        />
      </DialogContent>
      <DialogActions sx={{ pt: 0 }}>
        <Button onClick={onClose} color="inherit">
          {t('dialog.open-items.cancel')}
        </Button>
        <Button
          loading={isDownloading}
          onClick={handleDownload}
          variant="contained"
        >
          {t('dialog.open-items.download')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
